import React, { useState } from 'react';
import {
	Box, Typography, Paper, Select, MenuItem,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Search } from 'lucide-react';

const ledgerData = [
	{
		name: 'Inventory',
		category: 'Operational',
		balance: '$2.5M',
		transactions: 156,
		riskLevel: 'LOW',
		status: 'Active'
	},
	{
		name: 'Seller Wallet',
		category: 'Operational',
		balance: '$850K',
		transactions: 89,
		riskLevel: 'LOW',
		status: 'Active'
	},
	{
		name: 'Purchase',
		category: 'Operational',
		balance: '$750K',
		transactions: 123,
		riskLevel: 'MEDIUM',
		status: 'Active'
	},
	{
		name: 'Incentive Fee',
		category: 'Financial',
		balance: '$125K',
		transactions: 45,
		riskLevel: 'LOW',
		status: 'Active'
	},
	{
		name: 'Agency Fee',
		category: 'Financial',
		balance: '$75K',
		transactions: 34,
		riskLevel: 'LOW',
		status: 'Active'
	},
	{
		name: 'VePay Revenue',
		category: 'Financial',
		balance: '$450K',
		transactions: 178,
		riskLevel: 'LOW',
		status: 'Active'
	}
];

export default function AllLedgers() {
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('All Status');
	const [typeFilter, setTypeFilter] = useState('All Types');
	const [timeFilter, setTimeFilter] = useState('All Time');

	const getRiskLevelColor = (level) => {
		switch (level) {
			case 'LOW':
				return 'rgb(220 252 231)';
			case 'MEDIUM':
				return 'rgb(254 249 195)';
			case 'HIGH':
				return 'rgb(254 226 226)';
			default:
				return 'rgb(241 245 249)';
		}
	};

	const getRiskLevelTextColor = (level) => {
		switch (level) {
			case 'LOW':
				return 'rgb(22 163 74)';
			case 'MEDIUM':
				return 'rgb(202 138 4)';
			case 'HIGH':
				return 'rgb(220 38 38)';
			default:
				return 'rgb(71 85 105)';
		}
	};

	return (
		<Paper
			elevation={0}
			sx={{
				p: 3,
				borderRadius: 2,
				border: '1px solid',
				borderColor: 'grey.200',
				bgcolor: 'white'
			}}
		>
			<Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flex: 1,
						gap: 2,
						px: 3,
						py: 1,
						borderRadius: 2,
						bgcolor: 'grey.50',
						border: '1px solid',
						borderColor: 'grey.200',
					}}
				>
					<Search className="w-5 h-5 text-gray-400" />
					<input
						type="text"
						placeholder="Search ledgers..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="bg-transparent border-none outline-none w-full text-sm"
					/>
				</Box>

				<Select
					value={statusFilter}
					onChange={(e) => setStatusFilter(e.target.value)}
					size="small"
					sx={{ minWidth: 120 }}
				>
					<MenuItem value="All Status">All Status</MenuItem>
					<MenuItem value="Active">Active</MenuItem>
					<MenuItem value="Inactive">Inactive</MenuItem>
				</Select>

				<Select
					value={typeFilter}
					onChange={(e) => setTypeFilter(e.target.value)}
					size="small"
					sx={{ minWidth: 120 }}
				>
					<MenuItem value="All Types">All Types</MenuItem>
					<MenuItem value="Operational">Operational</MenuItem>
					<MenuItem value="Financial">Financial</MenuItem>
				</Select>

				<Select
					value={timeFilter}
					onChange={(e) => setTimeFilter(e.target.value)}
					size="small"
					sx={{ minWidth: 120 }}
				>
					<MenuItem value="All Time">All Time</MenuItem>
					<MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
					<MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
					<MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
				</Select>
			</Box>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>LEDGER NAME</TableCell>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>CATEGORY</TableCell>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>BALANCE</TableCell>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>TRANSACTIONS</TableCell>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>RISK LEVEL</TableCell>
							<TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>STATUS</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{ledgerData.map((ledger, index) => (
							<TableRow
								key={index}
								sx={{
									'&:hover': {
										bgcolor: 'grey.50',
										cursor: 'pointer'
									}
								}}
							>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{ledger.name}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" sx={{ color: 'text.secondary' }}>
										{ledger.category}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{ledger.balance}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" sx={{ color: 'text.secondary' }}>
										{ledger.transactions}
									</Typography>
								</TableCell>
								<TableCell>
									<Box
										sx={{
											display: 'inline-block',
											px: 2,
											py: 0.5,
											borderRadius: 1,
											bgcolor: getRiskLevelColor(ledger.riskLevel),
											color: getRiskLevelTextColor(ledger.riskLevel),
											fontSize: '0.75rem',
											fontWeight: 500,
										}}
									>
										{ledger.riskLevel}
									</Box>
								</TableCell>
								<TableCell>
									<Box
										sx={{
											display: 'inline-block',
											px: 2,
											py: 0.5,
											borderRadius: 1,
											bgcolor: 'rgb(220 252 231)',
											color: 'rgb(22 163 74)',
											fontSize: '0.75rem',
											fontWeight: 500,
										}}
									>
										{ledger.status}
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
}
